import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { ReferenceChip } from '@/components/ReferenceChip';
import { api } from '@/lib/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Printer, Download, Gem, Coins, Sparkles, Circle,
  PencilLine,
} from 'lucide-react';
import {
  formatINR, formatCarats, formatGrams, formatDateTime,
} from '@/lib/format';
import { extractError } from '@/lib/errors';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { RequestEditDialog } from '@/components/RequestEditDialog';
import EditedMarker from '@/components/EditedMarker';
import { EDITED_VALUE_CLASS, EDITED_ROW_CLASS } from '@/lib/editFlow';
import { useAuth } from '@/lib/auth';

export default function IssueMemo() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <AppShell
      title="Issue Memo"
      subtitle="Materials issued to the goldsmith. View / print / open linked entities."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="memo-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <Content id={id} />
    </AppShell>
  );
}

function Content({ id }) {
  const [memo, setMemo] = useState(null);
  const [pdfDialog, setPdfDialog] = useState({ open: false, mode: 'download' });
  const [pendingEditId, setPendingEditId] = useState(null);

  const reload = () => {
    api.get(`/goldsmith-issues/${id}`)
      .then(({ data }) => setMemo(data))
      .catch(() => setMemo({ error: true }));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  // Wave 3.51 — Detect whether this issue currently has an open edit
  // request so we can show a "locked" banner and hide the Request Edit
  // button until the SA acts.
  useEffect(() => {
    if (!memo || memo.error) return;
    api.get('/edit-requests/', { params: { role_scope: 'all' } })
      .then(({ data }) => {
        const open = (data || []).find(
          (r) =>
            r.entity_id === id &&
            r.entity_type === 'goldsmith_issue' &&
            ['pending_sa', 'sent_back_to_user'].includes(r.status),
        );
        setPendingEditId(open ? open.id : null);
      })
      .catch(() => setPendingEditId(null));
  }, [id, memo?.last_edited_at]);

  if (!memo) return <div className="text-slate-500 py-8">Loading…</div>;
  if (memo.error) return <div className="text-rose-600 py-8">Memo not found.</div>;

  const safeName = (memo.issue_memo_no || memo.issue_no || id || 'memo').replace(/\//g, '-');
  const topEdited = memo.edited_fields || {};

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Issue Memo No.</div>
            <div className="text-2xl font-mono font-semibold text-emerald-700" data-testid="memo-detail-no">{memo.issue_memo_no || memo.issue_no}</div>
            <div className="text-xs text-slate-500 mt-1">{formatDateTime(memo.created_at)} · by <span className="font-medium text-slate-700">{memo.created_by_email}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">Source: <Badge variant="secondary" className="text-[10px] capitalize">{(memo.source || 'goldsmith_page').replace('_', ' ')}</Badge></div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">To Goldsmith</div>
            <Link to={`/goldsmiths/${memo.goldsmith_id}`} className="font-medium text-[var(--ink-navy)] hover:underline" data-testid="memo-goldsmith-link">{memo.goldsmith_name}</Link>
            <div className="flex gap-2 mt-2 flex-wrap justify-end">
              <Button
                variant="outline" className="h-9" data-testid="memo-view-pdf"
                onClick={() => setPdfDialog({ open: true, mode: 'view' })}
              >
                <FileText size={14} className="mr-1.5" /> View PDF
              </Button>
              <Button
                variant="outline" className="h-9" data-testid="memo-print"
                onClick={() => setPdfDialog({ open: true, mode: 'print' })}
              >
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
              <Button
                variant="outline" className="h-9" data-testid="memo-download-pdf"
                onClick={() => setPdfDialog({ open: true, mode: 'download' })}
              >
                <Download size={14} className="mr-1.5" /> Download
              </Button>
              {/* Wave 3.51 — Request Edit (CA/SA only, within the T+1
                  IST window). Hidden automatically by the dialog
                  component when conditions are not met. Hidden ALSO if
                  there is already an open edit request for this issue
                  (lock state). */}
              {!pendingEditId && (
                <RequestEditDialog
                  entityType="goldsmith_issue"
                  entity={memo}
                  testId="memo-request-edit"
                  onSuccess={() => { reload(); }}
                />
              )}
            </div>
          </div>
        </div>
        {pendingEditId && (
          <div
            className="mt-3 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded flex items-center gap-2"
            data-testid="memo-edit-pending-banner"
          >
            <PencilLine size={14} />
            <span>
              <strong>Property locked</strong> — there is a pending Edit Request on
              this Issue Memo. Further movements (returns, attributions, deletes)
              are blocked until the Super Admin acts on it.
            </span>
            <Link
              to="/edit-requests"
              className="ml-auto text-amber-900 underline hover:no-underline"
              data-testid="memo-edit-pending-link"
            >
              Open Edit Requests
            </Link>
          </div>
        )}
        {(memo.reference || memo.note) && (
          <div className="mt-3 text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded">
            {memo.reference && (
              <span>
                <strong>Reference:</strong>{' '}
                <span className={topEdited.reference ? EDITED_VALUE_CLASS : ''}>
                  {memo.reference}
                </span>
                <EditedMarker show={!!topEdited.reference} />
              </span>
            )}
            {memo.reference && memo.note && <span> · </span>}
            {memo.note && (
              <span>
                <strong>Note:</strong>{' '}
                <span className={topEdited.note ? EDITED_VALUE_CLASS : ''}>
                  {memo.note}
                </span>
                <EditedMarker show={!!topEdited.note} />
              </span>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="text-base font-medium text-[var(--ink-navy)] mb-2">Line Items</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-right px-3 py-2">Quantity</th>
                <th className="text-right px-3 py-2">Value (₹)</th>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody data-testid="memo-lines-table">
              {(memo.lines || []).map((l, i) => {
                // Wave 3.25 hot-fix — Treat diamond/color_stone/pearl all as
                // carat-based materials (packet + carats + pieces). Previously
                // anything that wasn't 'diamond' fell through to the gold
                // branch, so colour-stone and pearl issue memos rendered with
                // empty Item and Qty=0 g (even though Value was correct).
                const mat = l.material_type;
                const isCarat = mat === 'diamond' || mat === 'color_stone' || mat === 'pearl';
                const matIcon = mat === 'diamond' ? <Gem size={12} className="inline mr-1 text-[var(--diamond-icon)]" />
                  : mat === 'color_stone' ? <Sparkles size={12} className="inline mr-1 text-[var(--colorstone-icon,#c2185b)]" />
                  : mat === 'pearl' ? <Circle size={12} className="inline mr-1 text-[var(--pearl-icon,#6B7280)]" />
                  : <Coins size={12} className="inline mr-1 text-[var(--gold-icon)]" />;
                const matLabel = mat === 'color_stone' ? 'Colour Stone' : mat === 'pearl' ? 'Pearl' : (mat || '—').replace(/_/g, ' ');
                // The packet route depends on material so the Item link
                // takes you to the correct master.
                const packetRoute = mat === 'diamond' ? '/diamonds/packets'
                  : mat === 'color_stone' ? '/color-stones/packets'
                  : mat === 'pearl' ? '/pearls/packets'
                  : null;
                const itemPillClass = mat === 'diamond' ? 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]'
                  : mat === 'color_stone' ? 'bg-pink-50 text-pink-700'
                  : mat === 'pearl' ? 'bg-slate-100 text-slate-700'
                  : '';
                return (
                  <tr
                    key={i}
                    className={`border-t border-[var(--divider)] ${
                      l.edited_fields && Object.keys(l.edited_fields).length > 0 ? EDITED_ROW_CLASS : ''
                    }`}
                    data-testid={`memo-line-${i}`}
                  >
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      {matIcon}
                      <span className="capitalize">{matLabel}</span>
                    </td>
                    <td className="px-3 py-2">
                      {isCarat && l.packet_id && packetRoute ? (
                        <Link
                          to={`${packetRoute}/${l.packet_id}`}
                          className={`font-mono text-[12px] ${itemPillClass} px-2 py-0.5 rounded hover:underline`}
                          data-testid={`memo-line-${i}-packet-link`}
                        >
                          {l.packet_no}
                        </Link>
                      ) : isCarat ? (
                        <span className="font-mono text-[12px]">{l.packet_no || '—'}</span>
                      ) : (
                        <span>{l.purity_name || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span
                        className={
                          (l.edited_fields?.carats || l.edited_fields?.pieces || l.edited_fields?.grams)
                            ? EDITED_VALUE_CLASS
                            : ''
                        }
                      >
                        {isCarat
                          ? `${formatCarats(l.carats || 0)} ct · ${l.pieces || 0} pcs`
                          : `${formatGrams(l.grams || 0)} g${l.purity_name ? ` (${l.purity_name})` : ''}`}
                      </span>
                      <EditedMarker
                        show={!!(l.edited_fields?.carats || l.edited_fields?.pieces || l.edited_fields?.grams)}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{formatINR(l.value)}</td>
                    <td className="px-3 py-2">
                      {l.order_id ? (
                        <Link to={`/orders/${l.order_id}`} className="text-[var(--ink-navy)] hover:underline" data-testid={`memo-line-${i}-order-link`}>
                          <span className={(l.edited_fields?.order_id || l.edited_fields?.requirement_id) ? EDITED_VALUE_CLASS : ''}>
                            {l.order_no}
                          </span>
                        </Link>
                      ) : (
                        <Badge
                          variant="secondary"
                          className={`text-[10px] ${l.edited_fields?.order_id ? 'bg-amber-100 text-amber-800' : ''}`}
                        >
                          Unattributed
                        </Badge>
                      )}
                      {l.order_id && l.order_title && (
                        <div
                          className="mt-0.5 text-[11px] font-medium text-emerald-700"
                          data-testid={`memo-line-${i}-order-title`}
                        >
                          {l.order_title}
                        </div>
                      )}
                      {l.order_id && l.customer_name && (
                        <div
                          className="mt-0.5 text-[11px] text-slate-600"
                          data-testid={`memo-line-${i}-customer`}
                        >
                          <span className="text-slate-400">Customer:</span>{' '}
                          <span className="font-medium text-[var(--ink-navy)]">{l.customer_name}</span>
                        </div>
                      )}
                      {memo.uses_reference_numbers && l.reference_no != null && (
                        <div className="mt-0.5">
                          <ReferenceChip
                            referenceNo={l.reference_no}
                            className="inline-flex items-center rounded bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 text-[10px] font-mono tabular-nums"
                            testId={`memo-line-${i}-reference`}
                            title={`${memo.reference_label || 'Page No.'} ${l.reference_no} — open in Order Forms (new tab)`}
                          >
                            {(memo.reference_label || 'Page No.')} {l.reference_no}
                          </ReferenceChip>
                        </div>
                      )}
                      <EditedMarker show={!!(l.edited_fields?.order_id || l.edited_fields?.requirement_id)} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      <span className={l.edited_fields?.note ? EDITED_VALUE_CLASS : ''}>{l.note}</span>
                      <EditedMarker show={!!l.edited_fields?.note} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-right mt-3 text-sm font-mono text-slate-700">
          Total: <span className="text-[var(--ink-navy)] font-medium">{formatINR(memo.total_value)}</span>
        </div>
      </Card>

      {(memo.lines || []).some((l) => (l.attributions?.length || l.returns?.length)) && (
        <Card className="p-5">
          <div className="text-base font-medium text-[var(--ink-navy)] mb-2">Lifecycle</div>
          <div className="space-y-4">
            {(memo.lines || []).map((l, i) => {
              if (!(l.attributions?.length || l.returns?.length)) return null;
              const isCarat = l.material_type === 'diamond' || l.material_type === 'color_stone' || l.material_type === 'pearl';
              return (
                <div key={i} className="border-l-2 border-[var(--divider)] pl-3">
                  <div className="text-sm font-medium text-[var(--ink-navy)]">
                    Line {i + 1}: {isCarat ? `${l.packet_no} (${formatCarats(l.carats || 0)} ct / ${l.pieces || 0} pcs)` : `${formatGrams(l.grams || 0)} g ${l.purity_name || ''}`}
                  </div>
                  <ul className="mt-1 space-y-1 text-xs text-slate-600">
                    {(l.attributions || []).map((a, j) => (
                      <li key={`a${j}`} data-testid={`memo-line-${i}-attr-${j}`}>
                        <Badge className="bg-blue-50 text-blue-700 text-[10px] mr-1">Attributed</Badge>
                        {isCarat
                          ? `${formatCarats(a.carats || 0)} ct / ${a.pieces || 0} pcs`
                          : `${formatGrams(a.grams || 0)} g`}
                        {' → '}
                        <Link to={`/orders/${a.order_id}`} className="text-[var(--ink-navy)] hover:underline">{a.order_no}</Link>
                        {a.note && <span className="text-slate-500"> · {a.note}</span>}
                        <span className="text-slate-400"> · {formatDateTime(a.ts)} by {a.by_email}</span>
                      </li>
                    ))}
                    {(l.returns || []).map((r, j) => (
                      <li key={`r${j}`} data-testid={`memo-line-${i}-return-${j}`}>
                        <Badge className="bg-rose-50 text-rose-700 text-[10px] mr-1">Returned</Badge>
                        {isCarat
                          ? `${formatCarats(r.carats || 0)} ct / ${r.pieces || 0} pcs`
                          : `${formatGrams(r.grams || 0)} g`}
                        {' from '}{r.source === 'unattributed' ? 'Unattributed' : <span className="text-[var(--ink-navy)]">order bucket</span>}
                        {r.return_memo_no && <span className="text-[var(--ink-navy)] font-mono ml-1">[{r.return_memo_no}]</span>}
                        <span className="text-slate-400"> · {formatDateTime(r.ts)} by {r.by_email}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <PdfDownloadDialog
        open={pdfDialog.open}
        onOpenChange={(o) => setPdfDialog({ ...pdfDialog, open: o })}
        title={pdfDialog.mode === 'view' ? 'View Issue Memo PDF' : pdfDialog.mode === 'print' ? 'Print Issue Memo' : 'Download Issue Memo PDF'}
        description="Choose whether to print your company logo and name on this memo."
        companyId={memo.company_id}
        endpoint={`/goldsmith-issues/${id}/pdf`}
        filename={`IssueMemo_${safeName}.pdf`}
        mode={pdfDialog.mode}
      />
    </div>
  );
}
